import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import noLogo from "../../../../assets/img/brand/noLogo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const creditNoteReport = (data = {}) => {
  
  const safeText = (v, fb = "—") => {
    const s = (v ?? "").toString().trim();
    return s.length ? s : fb;
  };

  const toNum = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const money = (v) =>
    Math.max(0, Math.round((toNum(v, 0) + Number.EPSILON) * 100) / 100);

  const fmtMoney = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(money(v));

  const formatDate = (d) => {
    const defaultDate = moment().subtract(1, "month").endOf("month");
    const m = moment.isMoment(d)
      ? d
      : d
        ? moment(d, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true)
        : defaultDate;
    return m.isValid() ? m.format("DD/MM/YYYY") : defaultDate.format("DD/MM/YYYY");
  };

  const layoutGrid = {
    hLineWidth: () => 0.8,
    vLineWidth: () => 0.8,
    hLineColor: () => "#000",
    vLineColor: () => "#000",
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 2,
    paddingBottom: () => 2,
  };

  const layoutNoBorder = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  };

  const makeLogoBlock = () => {
    if (data?.logoDataUrl) {
      return { image: data.logoDataUrl, width: 140, margin: [0, 0, 0, 0] };
    }

    return {
      layout: layoutNoBorder,
      width: 150,
      table: { widths: ["*"], body: [[{ image: noLogo, width: 140 }]] },
    };
  };

  const value = money(data?.value ?? 0);
  const itbis = money(data?.itbis ?? 0);
  const total = money(data?.total ?? value + itbis);
  const insurerRncRaw = (
    data?.insurerRnc ??
    data?.rnc ??
    data?.arsRnc ??
    data?.ars?.rnc ??
    ""
  )
    .toString()
    .trim();
  const voucherDueDateLabel = formatDate(data?.voucherDueDate);
  const generatedDateLabel = formatDate(
    data?.generatedAt || data?.dateLabel || data?.date,
  );
  const consultoryName =
    data?.consultoryName ?? data?.consultoryAddressLine1 ?? "Consultorio";
  const consultoryAddress =
    data?.consultoryAddress ?? data?.consultoryAddressLine2 ?? "Direccion";
  const consultorySector = (data?.consultoryAddressLine3 ?? "")
    .toString()
    .trim();
  const consultoryRncRaw = (data?.consultoryRnc ?? "").toString().trim();
  const consultoryHeaderLines = [
    {
      text: safeText(consultoryName, "Consultorio") + "\n",
      bold: true,
    },
    {
      text:
        safeText(consultoryAddress, "Direccion") +
        (consultorySector ? `\n${consultorySector}` : "") +
        "\n",
    },
  ];

  if (consultoryRncRaw) {
    consultoryHeaderLines.push(`RNC ${consultoryRncRaw}`);
  }

  return {
    pageSize: "LETTER",
    pageMargins: [30, 30, 30, 20],
    defaultStyle: { fontSize: 10 },
    styles: {
      title: { fontSize: 13, bold: true, decoration: "underline" },
      sectionTitle: { fontSize: 10, bold: true, decoration: "underline" },
      th: { bold: true, fontSize: 10 },
      td: { fontSize: 10 },
    },
    content: [
      {
        columns: [
          {
            width: 170,
            stack: [
              { ...makeLogoBlock() },
              {
                text: consultoryHeaderLines,
                margin: [0, 2, 0, 0],
                fontSize: 10,
              },
              {
                margin: [0, 12, 0, 0],
                text: [
                  { text: "Aseguradora: ", bold: true },
                  safeText(data?.insurerName),
                ],
              },
              ...(insurerRncRaw
                ? [{ text: [{ text: "RNC: ", bold: true }, insurerRncRaw] }]
                : []),
            ],
          },
          {
            width: "*",
            margin: [0, 30, 0, 0],
            fontSize: 13,
            stack: [
              {
                text: data?.voucherName || "Comprobante Fiscal",
                style: "title",
                alignment: "right",
                bold: true,
              },
              {
                text: `Fecha: ${generatedDateLabel}`,
                alignment: "right",
                margin: [0, 4, 0, 0],
                bold: true,
                fontSize: 10,
              },
              {
                text: [
                  { text: "NCF: ", bold: true },
                  { text: safeText(data?.ncf, ""), decoration: "underline" },
                  { text: "\n NCF a Aplicar: ", bold: true },
                  {
                    text: safeText(data?.applyNcf, ""),
                    decoration: "underline",
                  },
                  { text: "\n Valido Hasta: ", bold: true },
                  { text: voucherDueDateLabel, decoration: "underline" },
                ],
                alignment: "right",
                margin: [0, 30, 0, 0],
              },
            ],
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 6],
      },
      {
        text: "Concepto:",
        style: "sectionTitle",
        margin: [0, 2, 0, 3],
      },
      {
        table: {
          widths: ["*"],
          body: [[{ text: safeText(data?.concept, ""), style: "td" }]],
        },
        layout: layoutGrid,
        margin: [0, 0, 0, 12],
      },
      {
        columns: [
          { width: 307, text: "" },
          {
            width: 220,
            table: {
              widths: [80, 140],
              body: [
                [
                  { text: "Valor", style: "th", alignment: "center" },
                  { text: fmtMoney(value), style: "td", alignment: "right" },
                ],
                [
                  { text: "ITBIS", style: "th", alignment: "center" },
                  { text: fmtMoney(itbis), style: "td", alignment: "right" },
                ],
                [
                  {
                    text: "Total",
                    style: "th",
                    alignment: "center",
                    bold: true,
                  },
                  {
                    text: fmtMoney(total),
                    style: "td",
                    alignment: "right",
                    bold: true,
                  },
                ],
              ],
            },
            layout: layoutGrid,
          },
          { width: "*", text: "" },
        ],
      },
    ],
  };
};

export const openCreditNoteReport = (data = {}) => {
  pdfMake.createPdf(creditNoteReport(data)).open();
};
